import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { useTranslation } from 'react-i18next';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { printFileDetailMessage } from '../printFileDetail';

const CONCURRENCY = 4;
export type SelfHealKind = 'regular' | 'ondemand';
/** Mirrors `PrintFileTargetPhase` with the bulk-only 'queued' idle state. */
export type RowStatus = 'queued' | 'uploading' | 'verifying' | 'verified' | 'success' | 'error';
type Matrix = RouterOutputs['printAgentAdmin']['getPrintFileIssueTargets'];
type Target = Matrix['targets'][number];
export const uploadTargetKey = (issueId: string, revision: string, side: string, format: string) =>
  [issueId, revision, side, format].join(':');
export interface BulkUploadRow extends Omit<Target, 'status'> {
  issueId: string;
  targetRevision: string;
  kind: SelfHealKind;
  file: File | null;
  uploaded?: boolean;
  status: RowStatus;
  /** The matrix's own status, kept so the shared row renders identically to
   *  the dialog (including `unknown` — the bulk read does NOT probe storage). */
  targetStatus: Target['status'];
  detail?: string;
}
type Seed = { issueId: string; kind: SelfHealKind };

/** Target-keyed retry state. Each issue's targets run sequentially; unrelated
 * issues use a bounded pool so one failure cannot abort another issue. */
export function usePrintFileIssueBulkUpload() {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Record<string, BulkUploadRow>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const rowsRef = useRef(rows);
  const generation = useRef(0);
  const running = useRef(false);
  const upload = trpc.printAgentAdmin.uploadReplacementFile.useMutation();
  const verify = trpc.printAgentAdmin.resolveAfterUpload.useMutation();
  const replaceRows = (next: Record<string, BulkUploadRow>) => { rowsRef.current = next; setRows(next); };
  const patchRow = (key: string, patch: Partial<BulkUploadRow>) => {
    const row = rowsRef.current[key];
    if (row) replaceRows({ ...rowsRef.current, [key]: { ...row, ...patch } });
  };
  const toRow = (target: Target, seed: { issueId: string; revision: string; kind: SelfHealKind },
    previous: BulkUploadRow | undefined, resolved: boolean): BulkUploadRow => ({
    ...target, targetStatus: target.status, issueId: seed.issueId, targetRevision: seed.revision, kind: seed.kind,
    file: previous?.file ?? null, uploaded: previous?.uploaded, detail: previous?.detail,
    status: resolved ? 'success' : previous?.status ?? 'queued',
  });
  // Bulk reads use fetch rather than one React query per target. Observe their
  // refreshed cache values so SSE and post-verify invalidation update this UI
  // without discarding a successful PUT or a sibling's retry state.
  useEffect(() => queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    const issueId = Object.values(rowsRef.current).find((row) =>
      JSON.stringify(event.query.queryKey) === JSON.stringify(getQueryKey(trpc.printAgentAdmin.getPrintFileIssueTargets, { issueId: row.issueId }, 'query')))?.issueId;
    if (!issueId) return;
    const matrix = event.query.state.data as Matrix | undefined;
    if (!matrix?.targetRevision || !matrix.kind) return;
    const next = Object.fromEntries(Object.entries(rowsRef.current).filter(([, row]) => row.issueId !== issueId));
    for (const target of matrix.targets) {
      const key = uploadTargetKey(issueId, matrix.targetRevision, target.side, target.format);
      next[key] = toRow(target, { issueId, revision: matrix.targetRevision, kind: matrix.kind },
        rowsRef.current[key], matrix.resolved);
    }
    replaceRows(next);
  }), [queryClient]);
  // `includeDownloadUrls` is deliberately omitted (defaults false): the bulk
  // modal never links to an existing file, and asking for one would make the
  // server HEAD every target of every selected issue on open.
  const fetchMatrix = (issueId: string) => utils.printAgentAdmin.getPrintFileIssueTargets.fetch({ issueId });
  const initRows = async (seeds: Seed[]) => {
    const runGeneration = ++generation.current;
    setIsLoading(true);
    const next: Record<string, BulkUploadRow> = {};
    const failures: Record<string, string> = {};
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, seeds.length) }, async () => {
      while (cursor < seeds.length) {
        const seed = seeds[cursor++]!;
        try {
          const matrix = await fetchMatrix(seed.issueId);
          // A platen variant size is uploaded from Product updates only.
          if (matrix.platenVariantFileNeeded) throw new Error(t('issues.printFiles.platenVariant.uploadFromUpdates'));
          if (matrix.blockedReason) throw new Error(t('issues.printFiles.upload.metadataRequired'));
          if (!matrix.canResolve || !matrix.kind || !matrix.targetRevision) throw new Error(t('issues.printFiles.upload.fleetUnknown'));
          for (const target of matrix.targets) {
            const key = uploadTargetKey(seed.issueId, matrix.targetRevision, target.side, target.format);
            next[key] = toRow(target, { issueId: seed.issueId, revision: matrix.targetRevision, kind: matrix.kind },
              rowsRef.current[key], matrix.resolved);
          }
        } catch (error) { failures[seed.issueId] = getUserMessage(error); }
      }
    }));
    if (generation.current !== runGeneration) return;
    replaceRows(next); setErrors(failures); setIsLoading(false);
  };
  const setRowFile = (key: string, file: File | null) => patchRow(key, { file, uploaded: false, status: 'queued', detail: undefined });
  const reset = useCallback(() => { generation.current++; rowsRef.current = {}; setRows({}); setErrors({}); setIsUploading(false); setIsLoading(false); }, []);
  const run = async () => {
    if (running.current) return;
    running.current = true; setIsUploading(true);
    const runGeneration = generation.current;
    const issueIds = [...new Set(Object.values(rowsRef.current).map((row) => row.issueId))];
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, issueIds.length) }, async () => {
      while (cursor < issueIds.length && generation.current === runGeneration) {
        const issueId = issueIds[cursor++]!;
        const keys = Object.keys(rowsRef.current).filter((key) => rowsRef.current[key]!.issueId === issueId);
        for (const key of keys) {
          const row = rowsRef.current[key];
          if (!row?.file || row.status === 'success' || row.locked) continue;
          patchRow(key, { status: 'uploading', detail: undefined });
          try {
            const matrix = await fetchMatrix(issueId);
            if (generation.current !== runGeneration || !rowsRef.current[key]) break;
            if (matrix.targetRevision !== row.targetRevision) {
              // A new association must never reuse an old side's cached PUT.
              for (const staleKey of keys) patchRow(staleKey, { file: null, uploaded: false, status: 'error', detail: t('issues.printFiles.upload.targetsChanged') });
              throw new Error(t('issues.printFiles.upload.targetsChanged'));
            }
            if (!row.file.name.toLowerCase().endsWith(`.${row.format}`)) throw new Error(t('issues.printFiles.upload.extensionRequired', { format: row.format.toUpperCase() }));
            const identity = { issueId, side: row.side, uploadedFormat: row.format, targetRevision: row.targetRevision };
            if (!row.uploaded) {
              const minted = await upload.mutateAsync({ ...identity, kind: matrix.kind ?? row.kind });
              if (generation.current !== runGeneration || !rowsRef.current[key]) break;
              if (minted.targetRevision !== row.targetRevision) throw new Error(t('issues.printFiles.upload.targetsChanged'));
              const response = await fetch(minted.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: row.file });
              if (!response.ok) throw new Error(t('issues.printFiles.upload.putFailed', { status: response.status }));
              if (generation.current !== runGeneration || !rowsRef.current[key]) break;
              patchRow(key, { uploaded: true });
            }
            patchRow(key, { status: 'verifying' });
            const result = await verify.mutateAsync(identity);
            if (generation.current !== runGeneration || !rowsRef.current[key]) break;
            if (result.resolved) {
              for (const siblingKey of keys) patchRow(siblingKey, { status: 'success', detail: undefined });
              break;
            }
            patchRow(key, 'targetVerified' in result && result.targetVerified
              ? { status: 'verified', detail: t('issues.printFiles.upload.targetVerified') }
              : { status: 'error', detail: printFileDetailMessage(t, result) ?? t('issues.printFiles.upload.errorGeneric') });
          } catch (error) {
            if (generation.current === runGeneration) patchRow(key, { status: 'error', detail: getUserMessage(error) });
          } finally {
            // A `break` above (a stale generation, a row that vanished) leaves
            // the row mid-flight — never strand it on a spinner nothing will
            // clear. Only rows still in THIS run's map are ours to settle.
            const current = rowsRef.current[key];
            if (generation.current === runGeneration && (current?.status === 'uploading' || current?.status === 'verifying')) {
              patchRow(key, { status: 'queued' });
            }
            void utils.printAgentAdmin.getPrintFileIssueTargets.invalidate({ issueId }, { refetchType: 'all' });
            void utils.productionOrders.issues.list.invalidate();
            void utils.productionOrders.issues.getCounts.invalidate();
          }
        }
      }
    }));
    running.current = false; setIsUploading(false);
  };
  const list = Object.values(rows);
  // `staged` = rows the operator actually attached a file to and that are not
  // already done. The footer button counts THESE, not every required file —
  // "Upload 6 files" on a selection where only 2 dropzones are filled was a
  // promise the run could not keep.
  const staged = list.filter((row) => row.file && !row.locked && row.status !== 'success');
  return { rows, errors, isLoading, isUploading, initRows, setRowFile, reset, run,
    succeeded: list.filter((row) => row.status === 'success').length,
    failed: list.filter((row) => row.status === 'error').length,
    total: list.length,
    stagedCount: staged.length,
    anyRowHasFile: staged.length > 0,
  };
}
